import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18next from 'i18next';
import type {
  InitCompanyDto,
  CompanyDto,
  UpdateCompanyDto,
  CompanyLogoType,
} from '@monorepo/shared';
import { apiClient } from '../lib/axios';
import { queryClient } from '../lib/queryClient';

export const companyApi = {
  initCompany: async (data: InitCompanyDto): Promise<CompanyDto> => {
    const { data: response } = await apiClient.post<CompanyDto>('/company/init', data);
    return response;
  },

  getCompany: async (companyId: string): Promise<CompanyDto> => {
    const { data: response } = await apiClient.get<CompanyDto>(`/company/${companyId}`);
    return response;
  },

  updateCompany: async (companyId: string, data: UpdateCompanyDto): Promise<CompanyDto> => {
    const { data: response } = await apiClient.put<CompanyDto>(`/company/${companyId}`, data);
    return response;
  },

  requestSirenUpdate: async (
    companyId: string,
    data: { oldSiren: string; newSiren: string; companyName: string },
  ): Promise<void> => {
    await apiClient.post(`/company/${companyId}/request-siren-update`, data);
  },

  uploadCompanyLogo: async (
    companyId: string,
    type: CompanyLogoType,
    file: File,
  ): Promise<CompanyDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data: response } = await apiClient.post<CompanyDto>(
      `/company/${companyId}/logo/${type}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response;
  },

  deleteCompanyLogo: async (companyId: string, type: CompanyLogoType): Promise<CompanyDto> => {
    const { data: response } = await apiClient.delete<CompanyDto>(
      `/company/${companyId}/logo/${type}`,
    );
    return response;
  },
};

export const useInitCompany = () => {
  return useMutation({
    mutationFn: companyApi.initCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
};

export const useGetCompany = (companyId: string) => {
  return useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companyApi.getCompany(companyId),
    enabled: !!companyId,
  });
};

export const useUpdateCompany = (companyId: string) => {
  return useMutation({
    mutationFn: (data: UpdateCompanyDto) => companyApi.updateCompany(companyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      toast.success(i18next.t('company.update_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useRequestSirenUpdate = (companyId: string) => {
  return useMutation({
    mutationFn: (data: { oldSiren: string; newSiren: string; companyName: string }) =>
      companyApi.requestSirenUpdate(companyId, data),
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useUploadCompanyLogo = (companyId: string) => {
  return useMutation({
    mutationFn: ({ type, file }: { type: CompanyLogoType; file: File }) =>
      companyApi.uploadCompanyLogo(companyId, type, file),
    onSuccess: (updatedCompany) => {
      queryClient.setQueryData(['company', companyId], updatedCompany);
      toast.success(i18next.t('company.logo.upload_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};

export const useDeleteCompanyLogo = (companyId: string) => {
  return useMutation({
    mutationFn: (type: CompanyLogoType) => companyApi.deleteCompanyLogo(companyId, type),
    onSuccess: (updatedCompany) => {
      queryClient.setQueryData(['company', companyId], updatedCompany);
      toast.success(i18next.t('company.logo.delete_success'));
    },
    onError: () => toast.error(i18next.t('common.error')),
  });
};
